import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/integrations/supabase/types";

export interface HomeStats {
  /** Unika aktiva elavtal från avtalsflödet. */
  agreementCount: number;
  /** Unika elhandelsbolag med aktiva avtal. */
  companyCount: number;
}

let cache: HomeStats | null = null;
let cacheAt = 0;
const TTL_MS = 5 * 60 * 1000; // 5 minuter

async function fetchHomeStats(): Promise<HomeStats> {
  if (cache && Date.now() - cacheAt < TTL_MS) return cache;

  const url = process.env["SUPABASE_URL"] ?? process.env["VITE_SUPABASE_URL"];
  const key =
    process.env["SUPABASE_PUBLISHABLE_KEY"] ?? process.env["VITE_SUPABASE_PUBLISHABLE_KEY"];
  if (!url || !key) {
    return { agreementCount: 0, companyCount: 0 };
  }

  const client = createClient<Database>(url, key, {
    auth: { persistSession: false, autoRefreshToken: false },
  });

  // Endast privatavtal – företagsavtal räknas inte med.
  const { count: agreementCount, error: agreementError } = await client
    .from("agreement_feed_items")
    .select("id", { count: "exact", head: true })
    .eq("is_private", true);

  if (agreementError) {
    return { agreementCount: 0, companyCount: 0 };
  }

  // Räkna unika elhandelsbolag med privatavtal (paginerat).
  const companies = new Set<string>();
  const pageSize = 1000;
  for (let from = 0; from < 30000; from += pageSize) {
    const { data, error } = await client
      .from("agreement_feed_items")
      .select("company_name")
      .eq("is_private", true)
      .range(from, from + pageSize - 1);
    if (error) break;
    for (const row of data ?? []) {
      if (row.company_name) companies.add(row.company_name.trim().toLowerCase());
    }
    if (!data || data.length < pageSize) break;
  }

  const stats = {
    agreementCount: agreementCount ?? 0,
    companyCount: companies.size,
  };



  cache = stats;
  cacheAt = Date.now();
  return stats;
}

export const getHomeStats = createServerFn({ method: "GET" }).handler(async (): Promise<HomeStats> => {
  return fetchHomeStats();
});
